import "./TruncatedText.css";

import { useIsTextTruncated } from "../../../utils/hooks/useIsTextTruncated";
import { ChartTooltip } from "../../charts/PieChart/components/ChartTooltip";
import { ChartVariant } from "../../charts/utils";

type TruncatedTextClass = "body1" | "body2" | "body3" | "body4" | "body5";

interface TruncatedTextProps {
  text: string;
  /** Typography class for the text itself. */
  textClass?: TruncatedTextClass;
  /** Typography class for the full text in the tooltip. */
  tooltipTextClass?: TruncatedTextClass;
  /** Extra class on the text element, for the caller's own layout or colour rules. */
  className?: string;
  /** The brand the tooltip takes its chrome from. Omit to read it from the chart variant provider. */
  variant?: ChartVariant;
  /**
   * Whether cut text takes keyboard focus so the tooltip can be revealed without a pointer. Turn it
   * off only where a focusable ancestor already reveals it — a legend row is a button, and a second
   * tab stop inside it would be invalid.
   */
  isFocusable?: boolean;
}

/**
 * Text that tells you what it hid.
 *
 * The text is cut to one line with an ellipsis rather than allowed to reflow its container, so it
 * has to be recoverable: the tooltip is rendered only when the text is actually cut, which keeps it
 * meaningful instead of firing on every instance, and CSS reveals it on hover or keyboard focus. Only
 * cut text is focusable, so text that fits adds no tab stop.
 */
export const TruncatedText = ({
  text,
  textClass = "body3",
  tooltipTextClass = "body3",
  className,
  variant,
  isFocusable = true,
}: TruncatedTextProps): JSX.Element => {
  const { ref, isTruncated } = useIsTextTruncated(text);

  return (
    // Focusable only to reveal the tooltip for keyboard users; screen readers already get the full
    // text, since the ellipsis is visual only.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
    <span className="TruncatedText" tabIndex={isTruncated && isFocusable ? 0 : undefined}>
      <p className={`${textClass} TruncatedText_text${className ? ` ${className}` : ""}`} ref={ref}>
        {text}
      </p>
      {isTruncated && (
        <ChartTooltip placement="AboveLabel" variant={variant}>
          <p className={tooltipTextClass}>{text}</p>
        </ChartTooltip>
      )}
    </span>
  );
};
